import { HAMMER_CATEGORY_SPREAD_BOOKS } from 'au-js-sdk/lib/models/Product';
import { OptionDetails } from 'components/DetailsBlock';
import { format, parse } from 'date-fns';
import { optionGet } from 'faunctions';
import {
  calculateAdditionalPagePriceFromCartItem,
  calculatePriceForPrintSetCartItem,
  getBasePageCount,
  getPricePerPageForBookProductCartItem,
  HAS_PRINTS,
  PRINT_QUANTITY
} from 'features/pdp';
import { getAdditionalOptionPriceFromCartItemOption } from 'helpers/cart';
import { numberToUSD } from 'helpers/currency';
import { stripSnakeCase, toTitleCase } from 'helpers/strings';

import { CompactProject } from 'hooks/useProjects';
import { HIDDEN_OPTIONS } from 'store/global/constants';
import { AUQuoteDataCartItem, CustomOption } from 'types/magento';
import { Product } from 'types/Product';
import { CartItemOptionsDetails } from './CartItem/CartItemOptionDetails';
import { isSpreadBook } from 'helpers/products';
import { SKU_BABY_BOARD_BOOK, SKU_EVERYDAY_PHOTO_BOOK } from 'helpers/projects/redirect';

const LAST_MODIFIED_OPTION_PREFIX = 'Last Modified: ';
const PRODUCTS_WITH_FIXED_PAGE_COUNTS = [SKU_EVERYDAY_PHOTO_BOOK, SKU_BABY_BOARD_BOOK];

export const getHammerCategory = (item: AUQuoteDataCartItem): string => {
  return item.extension_attributes.hammer_category || item.reporting_product_category;
};

/**
 * formatProductOption
 * Takes an option from an AUQuoteDataCartItem's extension attributes
 * Returns string with it's name and value, and optionally the additional price added
 *
 * @param option
 * @param additionalPrice
 * @returns
 */
// TODO strikethrough pricing on options?
export const formatProductOption = (option: CustomOption, additionalPrice?: number): OptionDetails => ({
  optionString: `${toTitleCase(stripSnakeCase(option.name))}: ${toTitleCase(option.value)}`,
  additionalPrice
});

const formatPageCount = (count: number, item: AUQuoteDataCartItem): string =>
  `${getHammerCategory(item) === HAMMER_CATEGORY_SPREAD_BOOKS ? 'Spreads' : 'Page Count'}: ${count}`;

const formatAdditionalPageCount = (
  additionalPageCount: number,
  additionalPageCountPrice: number,
  additonalPricePerPage: number
): string => {
  return additionalPageCount > 0 && additionalPageCountPrice > 0
    ? `${additionalPageCount} Added Pages @ ${numberToUSD(additonalPricePerPage)}/ Page`
    : '';
};

const getAdditionalPageCountString = (
  project: CompactProject,
  cartItemProduct: Product,
  item: AUQuoteDataCartItem,
  additionalPageCountPrice: number
): string => {
  const lowestPageCount = getBasePageCount(project, cartItemProduct, true);
  const additionalPageCount = project?.product?.pageCount - lowestPageCount;
  let additonalPricePerPage = getPricePerPageForBookProductCartItem(cartItemProduct, item);

  //handle special case for BBB which has set page counts in the editor/pdp
  if (additionalPageCountPrice && additionalPageCount && additonalPricePerPage === 0) {
    additonalPricePerPage = additionalPageCountPrice / additionalPageCount;
  }

  return formatAdditionalPageCount(additionalPageCount, additionalPageCountPrice, additonalPricePerPage);
};

/**
 * formatPageCountOption
 * Finds the page count from the project's product.pageCount
 * Finds price for any additional pages
 * Returns string with the page count and the additional price if any
 *
 * @param project
 * @param cartItemProduct
 * @param item
 * @returns
 */
export const formatPageCountOption = (
  project: CompactProject,
  cartItemProduct: Product,
  item: AUQuoteDataCartItem
): OptionDetails => {
  let additionalPageCountPrice = calculateAdditionalPagePriceFromCartItem(project, cartItemProduct, item);

  //the problem is that the BBB already has the page count price as in pages whereas the layflat has it in spreads
  if (isSpreadBook(cartItemProduct.hammerCategory) && !PRODUCTS_WITH_FIXED_PAGE_COUNTS.includes(cartItemProduct.sku)) {
    additionalPageCountPrice *= 2;
  }

  const additionalPageCountPriceString = getAdditionalPageCountString(
    project,
    cartItemProduct,
    item,
    additionalPageCountPrice
  );

  return {
    optionString: formatPageCount(project?.product?.pageCount, item),
    additionalPrice: additionalPageCountPrice,
    additionalPageCountPriceString: additionalPageCountPriceString,
    addtionalStrNewLine: true
  };
};

/**
 * formatPageCountOption
 * Finds the page count from the project's product.pageCount
 * Finds price for any additional pages
 * Returns string with the page count and the additional price if any
 *
 * @param project
 * @param cartItemProduct
 * @param item
 * @returns
 */
export const formatPrintQuantityCountOption = (
  printQuantity: number,
  cartItemProduct: Product,
  productOptions: CustomOption[]
): OptionDetails => {
  const additionalPrintQuantityCountPrice = calculatePriceForPrintSetCartItem(cartItemProduct, productOptions);
  const pricePerPrint = additionalPrintQuantityCountPrice / printQuantity;

  return {
    optionString: `${printQuantity} Prints`,
    additionalPrice: additionalPrintQuantityCountPrice,
    additionalPageCountPriceString: `${printQuantity} Added Prints @ ${numberToUSD(pricePerPrint)}/ Page`,
    addtionalStrNewLine: true
  };
};

const addGiftCardOptions = (productOptions: OptionDetails[], item: AUQuoteDataCartItem): OptionDetails[] => {
  const giftCardNotes = [
    {
      label: 'Amount',
      value: item?.extension_attributes?.giftcard_amount && numberToUSD(item?.extension_attributes?.giftcard_amount)
    },
    { label: 'Sender Name', value: item?.extension_attributes?.giftcard_sender_name },
    { label: 'Recipient Name', value: item?.extension_attributes?.giftcard_recipient_name },
    { label: 'Recipient Email', value: item?.extension_attributes?.giftcard_recipient_email },
    {
      label: 'Date to Send',
      value:
        item?.extension_attributes?.giftcard_delivery_date &&
        parse(item?.extension_attributes?.giftcard_delivery_date, 'yyyy-MM-dd', new Date()).toLocaleDateString()
    },
    { label: 'Personal Note', value: item?.extension_attributes?.giftcard_message }
  ].filter((giftCardNote) => giftCardNote.value);

  giftCardNotes.forEach((giftCardNote) => {
    productOptions.push({
      optionString: `${toTitleCase(stripSnakeCase(giftCardNote.label))}: ${toTitleCase(giftCardNote.value)}`
    });
  });

  return productOptions;
};

const addLastModifiedOption = (productOptions: OptionDetails[], project: CompactProject): OptionDetails[] => {
  if (
    project?.schemaVersion === 2 &&
    project?.lastModifiedAt &&
    !productOptions.find((o) => o.optionString?.includes(LAST_MODIFIED_OPTION_PREFIX))
  ) {
    productOptions.push({
      optionString: LAST_MODIFIED_OPTION_PREFIX + format(new Date(project.lastModifiedAt), 'MM/dd/yyyy h:mm a')
    });
  }

  return productOptions;
};

/**
 * getVisibleOptionTitles
 * Returns array excluding options we need hidden from the user
 * Similar hidden behavior found in PDP, Cart, Order Details, Recent Projects
 * Example: print_border is per print so it doesn't make sense to show for entire product
 *
 * @param options
 * @returns
 */
export const getVisibleOptions = (options: Partial<CustomOption>[]): any[] =>
  options.filter((option) => !HIDDEN_OPTIONS.includes(option?.option_title));

/**
 * getProductOptions
 *
 * Grabs options from AUQuoteDataCartItem's extension attributes
 * Formats to display the option's name, value, and optionally the additional price for modifications
 *
 * Some products have a "Base Page Count", this ensures they display actual "Page Count" instead
 *
 * Set Page Count option on books if it is missing from AUQuoteDataCartItem's extension attributes
 * This is required when the project is built without changing the page count.
 *
 * Adds options for gift cards
 * Adds last modified option
 *
 * @param project
 * @param cartItemProduct
 * @param item
 * @returns OptionDetails[]
 */
export const getProductOptions = (
  project: CompactProject,
  cartItemProduct: Product,
  item: AUQuoteDataCartItem
): OptionDetails[] => {
  let hasPageCountOption = false;

  let productOptions: OptionDetails[] = optionGet('product_option.extension_attributes.readable')(item)
    .map((x: any) => {
      return x.reduce((accum, cur) => {
        if (HIDDEN_OPTIONS.includes(cur.option_title)) {
          return [...accum];
        }

        const additionalPrice = getAdditionalOptionPriceFromCartItemOption(item, cartItemProduct, cur);
        let optionDetails: OptionDetails;

        if (cur.name.toLowerCase().includes('page')) {
          optionDetails = formatPageCountOption(project, cartItemProduct, item);
          hasPageCountOption = true;
        } else {
          optionDetails = formatProductOption(cur, additionalPrice);
        }

        return [...accum, optionDetails];
      }, []);
    })
    .getOrElse([]);

  if (!hasPageCountOption && project?.product?.hammerCategory.includes('books')) {
    productOptions.push(formatPageCountOption(project, cartItemProduct, item));
  }

  productOptions = addGiftCardOptions(productOptions, item);
  productOptions = addLastModifiedOption(productOptions, project);

  return productOptions;
};

/**
 * getCartItemPriceDetails
 *
 * Similar to getProductOptions above, but specifically formatted for the
 * CartItemOptionDetails component as needed for the test FEATURE_FLAG_CART_PRICE_TRANSPARENCY
 *
 * @param project
 * @param cartItemProduct
 * @param item
 * @returns OptionPriceDetails[]
 */
export const getCartItemOptionPriceDetails = (
  project: CompactProject,
  cartItemProduct: Product,
  item: AUQuoteDataCartItem,
  isBrassWoodDisplayBoxWithPrints: boolean
): CartItemOptionsDetails => {
  const productOptions = optionGet('product_option.extension_attributes.readable')(item).getOrElse([]);
  const cartItemOptionsDetails: CartItemOptionsDetails = {
    defaultProductOptions: [],
    upchargedProductOptions: []
  };

  const dependentUpchargedProductOptions = [];
  let hasPageCountOption = false;

  productOptions.forEach((cartItemOption) => {
    const productOption = cartItemProduct.customOptions?.find((option) => option.name === cartItemOption.option_title);

    if (!productOption || HIDDEN_OPTIONS.includes(cartItemOption.option_title)) {
      return;
    }

    if (cartItemOption.name.toLowerCase().includes('page')) {
      hasPageCountOption = handlePageCountOption(
        project,
        cartItemProduct,
        item,
        cartItemOption,
        cartItemOptionsDetails
      );
      return;
    }

    if (cartItemOption.option_title.toLowerCase().includes(PRINT_QUANTITY)) {
      handlePrintQuantityOption(cartItemProduct, productOptions, cartItemOption, cartItemOptionsDetails);
      return;
    }

    const additionalPrice = getAdditionalOptionPriceFromCartItemOption(item, cartItemProduct, cartItemOption);

    if (productOption?.depends_on_option?.length > 0) {
      handleDependentUpchargeOption(cartItemOption, productOption, additionalPrice, dependentUpchargedProductOptions);
      return;
    }

    handleDefaultOrUpchargedOption(
      cartItemOption,
      additionalPrice,
      isBrassWoodDisplayBoxWithPrints,
      cartItemOptionsDetails
    );
  });

  addMissingPageCountOptionIfNecessary(hasPageCountOption, project, cartItemProduct, item, cartItemOptionsDetails);

  attachDependentOptionsToParents(dependentUpchargedProductOptions, cartItemOptionsDetails);

  return cartItemOptionsDetails;
};

const handlePageCountOption = (
  project: CompactProject,
  product: Product,
  item: AUQuoteDataCartItem,
  option: CustomOption,
  details: CartItemOptionsDetails
): boolean => {
  const formatted = formatPageCountOption(project, product, item);
  if (formatted.additionalPrice > 0) {
    details.upchargedProductOptions.push({
      optionName: option.name,
      optionTitle: option.option_title,
      optionValue: option.value,
      additionalPrice: formatted.additionalPrice,
      additionalPageCountPriceString: formatted.additionalPageCountPriceString
    });
  }
  return true;
};

const handlePrintQuantityOption = (
  product: Product,
  allOptions: CustomOption[],
  option: CustomOption,
  details: CartItemOptionsDetails
): void => {
  const formatted = formatPrintQuantityCountOption(parseInt(option.value), product, allOptions);
  if (formatted.additionalPrice > 0) {
    details.upchargedProductOptions.push({
      optionName: option.name,
      optionTitle: option.option_title,
      optionValue: option.value,
      additionalPrice: formatted.additionalPrice,
      additionalPageCountPriceString: formatted.additionalPageCountPriceString
    });
  }
};

const handleDependentUpchargeOption = (option: CustomOption, productOption, additionalPrice: number, store): void => {
  store.push({
    optionName: option.name,
    optionTitle: option.option_title,
    optionValue: option.value,
    additionalPrice,
    dependsOnName: productOption.depends_on_option,
    dependsOnValue: productOption.depends_on_values
  });
};

const handleDefaultOrUpchargedOption = (
  option: CustomOption,
  price: number,
  isBWDB: boolean,
  details: CartItemOptionsDetails
): void => {
  if (price === 0) {
    if (isBWDB && option.option_title === HAS_PRINTS) {
      return;
    }
    details.defaultProductOptions.push({
      optionName: option.name,
      optionTitle: option.option_title,
      optionValue: option.value,
      additionalPrice: price
    });
  } else {
    details.upchargedProductOptions.push({
      optionName: option.name,
      optionTitle: option.option_title,
      optionValue: option.value,
      additionalPrice: price
    });
  }
};

const addMissingPageCountOptionIfNecessary = (
  hasPageCountOption: boolean,
  project: CompactProject,
  product: Product,
  item: AUQuoteDataCartItem,
  details: CartItemOptionsDetails
): void => {
  const additionalPrice = calculateAdditionalPagePriceFromCartItem(project, product, item);
  const formatted = formatPageCountOption(project, product, item);
  if (!hasPageCountOption && additionalPrice && project?.product?.hammerCategory.includes('books')) {
    details.upchargedProductOptions.push({
      optionName: formatted.optionString,
      optionTitle: formatted.optionString,
      optionValue: formatted.optionString,
      additionalPrice: formatted.additionalPrice,
      additionalPageCountPriceString: formatted.additionalPageCountPriceString
    });
  }
};

const attachDependentOptionsToParents = (dependentOptions, details: CartItemOptionsDetails): void => {
  dependentOptions.forEach((dependentOption) => {
    const parent = [...details.upchargedProductOptions, ...details.defaultProductOptions].find(
      (opt) => opt.optionTitle === dependentOption.dependsOnName
    );

    if (parent) {
      parent.dependentOptions = [dependentOption, ...(parent.dependentOptions || [])];
    }
  });
};
